export const GUEST_FORM_DATE_FORMAT = {
  ISO_DATE: 'yyyy-MM-dd',
  STAY_DAY_LABEL: 'EEE, dd MMM yyyy',
} as const;

export const GUEST_FORM_ROOM = {
  DEFAULT_ROOM_TYPE: 'Suite Room',
  OTHER_ROOM_TYPE: 'Other',
} as const;

export const GUEST_FORM_REFUND_STATUS = {
  YES: 'yes',
  NO: 'no',
} as const;

export const GUEST_FORM_STAFF_OPTIONS = [
  'Chandan',
  'Sibu',
  'Saantu',
  'Subrat',
  'Babu',
  'Bapi',
  'Bapuji',
] as const;

export const GUEST_FORM_VALIDATION_LIMIT = {
  NAME_MAX_LENGTH: 150,
  PHONE_MAX_LENGTH: 20,
  EMAIL_MAX_LENGTH: 150,
  BOOKED_BY_MAX_LENGTH: 150,
  TEXT_MAX_LENGTH: 500,
  NOTE_MAX_LENGTH: 250,
  ADULT_MAX_COUNT: 100,
  CHILD_MAX_COUNT: 100,
  DRIVER_MAX_COUNT: 20,
  MAX_AMOUNT: 10_000_000,
} as const;

export type GuestFormRefundStatus =
  typeof GUEST_FORM_REFUND_STATUS[keyof typeof GUEST_FORM_REFUND_STATUS];

export const GUEST_FORM_TEXT = {
  BUTTON_BACK: 'Back',
  BUTTON_CANCEL: 'Cancel',
  BUTTON_CREATE_BOOKING: 'Create Booking',
  BUTTON_UPDATE_BOOKING: 'Update Booking',
  BUTTON_ADD_ROOM: 'Add Room',
  BUTTON_REVIEW: 'Review',
  BUTTON_CONFIRM_SAVE: 'Confirm & Save',
  BUTTON_ATTACH_DOCUMENTS: 'Attach Photos / Documents',
  TOOLTIP_REMOVE_ROOM: 'Remove room',
  PAGE_TITLE_NEW: 'New Booking',
  PAGE_TITLE_EDIT: 'Edit Booking',
  SECTION_GUEST_INFO: 'Guest Information',
  SECTION_DATES_GUESTS: 'Booking Dates & Guests',
  SECTION_ROOM_ALLOCATION: 'Day-wise Room Allocation',
  SECTION_FINANCIAL: 'Financial & Other Details',
  SECTION_DOCUMENTS: 'Documents & Payment Proof',
  DRIVER_CHARGE: 'Driver Charge',
  DRIVER_CHARGE_DESCRIPTION_PREFIX: 'Per driver/day x',
  DRIVER_CHARGE_DESCRIPTION_SUFFIX: 'driver(s)',
  EXISTING_DOCUMENTS: 'Already uploaded:',
  PENDING_DOCUMENTS: 'To be uploaded on save:',
  DOCUMENT_HELPER: 'Images are auto-compressed. Max 10 MB per file. JPEG, PNG, WebP, GIF, PDF accepted.',
  NO_ROOMS_AVAILABLE: 'No rooms available for this type on this date',
  STATUS_SELECT: 'Select Status',
  REFUND_SELECT: '— Select —',
  YES: 'Yes',
  NO: 'No',
  AMOUNT_SYSTEM: 'System',
  AMOUNT_ENTERED: 'Entered',
  AMOUNT_DIFFERENCE: 'Difference',
} as const;

export const GUEST_FORM_FIELD_LABEL = {
  GUEST_NAME: 'Guest Name',
  PHONE: 'Phone Number',
  EMAIL: 'Email',
  ADDRESS: 'Address',
  CHECKIN_DATE: 'Check-in Date',
  CHECKOUT_DATE: 'Check-out Date',
  BOOKING_TYPE: 'Booking Type',
  NO_ADULTS: 'No. of Adults',
  NO_CHILDREN: 'No. of Children',
  NO_DRIVERS: 'No. of Drivers',
  ROOM_TYPE: 'Room Type',
  CUSTOM_ROOM_TYPE: 'Custom Room Type',
  ROOM_NUMBERS: 'Room Numbers',
  ROOM_NUMBER: 'Room Number',
  NO_OF_ROOMS: 'No. of Rooms',
  NO_ADULT_IN_ROOM: 'No. of Adult',
  NO_CHILDREN_IN_ROOM: 'No. of Children',
  AMOUNT_PER_PERSON: 'Amt/Person (₹)',
  DRIVER_AMOUNT_PER_DAY: 'Amount/Driver/Day (₹)',
  ADVANCE_AMOUNT: 'Advance Amount (₹)',
  TOTAL_AMOUNT: 'Total Amount (₹)',
  BOOKED_BY: 'Booked By',
  STATUS_REQUIRED: 'Status *',
  CANCELLATION_REASON: 'Cancellation Reason',
  IS_REFUNDED_REQUIRED: 'Is Refunded? *',
  REFUNDED_AMOUNT: 'Refunded Amount (₹)',
  REMARKS: 'Remarks',
  ADJUSTMENT_REASON: 'Adjustment reason',
  OPTIONAL_NOTE: 'Optional note',
} as const;

export const GUEST_FORM_MESSAGE = {
  LOAD_BOOKING_FAILED: 'Failed to load booking.',
  NAME_REQUIRED: 'Name is required',
  NAME_TOO_LONG: 'Guest name must be 150 characters or less',
  PHONE_REQUIRED: 'Phone is required',
  PHONE_INVALID: 'Invalid phone number',
  PHONE_TOO_LONG: 'Phone number must be 20 characters or less',
  EMAIL_INVALID: 'Invalid email',
  EMAIL_TOO_LONG: 'Email must be 150 characters or less',
  CHECKIN_REQUIRED: 'Check-in date required',
  CHECKOUT_REQUIRED: 'Check-out date required',
  CHECKOUT_BEFORE_CHECKIN: 'Check-out date cannot be before check-in',
  CHECKIN_IN_PAST: 'Check-in date cannot be in the past for a new booking',
  DAY_BOOKING_REQUIRED: 'At least one day booking is required',
  STATUS_REQUIRED: 'Status is required',
  ADULT_REQUIRED: 'At least 1 adult is required',
  CHILDREN_NEGATIVE: 'Children count cannot be negative',
  DRIVERS_NEGATIVE: 'Drivers count cannot be negative',
  GUEST_COUNT_TOO_LARGE: 'Guest count is too large',
  TOTAL_AMOUNT_REQUIRED: 'Total amount is required',
  TOTAL_AMOUNT_NEGATIVE: 'Total amount cannot be negative',
  ADVANCE_AMOUNT_NEGATIVE: 'Advance amount cannot be negative',
  ADVANCE_EXCEEDS_TOTAL: 'Advance amount cannot be greater than total amount',
  AMOUNT_TOO_LARGE: 'Amount is too large',
  BOOKED_BY_REQUIRED: 'Booked by is required',
  BOOKED_BY_TOO_LONG: 'Booked by must be 150 characters or less',
  TEXT_TOO_LONG: 'Text field is too long',
  CANCELLATION_REASON_REQUIRED: 'Cancellation reason is required',
  REFUND_SELECTION_REQUIRED: 'Please select whether refund was issued',
  REFUNDED_AMOUNT_REQUIRED: 'Refunded amount is required',
  REFUNDED_AMOUNT_EXCEEDS_ADVANCE: 'Refunded amount cannot be greater than advance amount',
  BOOKING_SAVED_UPLOAD_FAILED: 'Booking saved, but file upload failed.',
  BOOKING_CREATED_UPLOAD_FAILED: 'Booking created, but file upload failed.',
  BOOKING_UPDATED: 'Booking updated successfully!',
  BOOKING_CREATED: 'Booking created successfully!',
  UPDATE_FAILED: 'Failed to update.',
  CREATE_FAILED: 'Failed to create booking.',
  AMOUNT_REASON_REQUIRED: 'Select a reason for the amount adjustment.',
  TOTAL_AMOUNT_HELPER_PREFIX: 'Auto-calculated from rooms; edit if adjustment is needed. Children are charged at',
  CHILD_RATE_HELPER_PREFIX: 'Child',
  ROW_TOTAL_PREFIX: 'Row ₹',
  CONFIRM_AMOUNT_TITLE: 'Confirm Total Amount',
  CONFIRM_AMOUNT_BODY: 'The entered total amount is different from the system-calculated amount.',
  CONFIRM_AMOUNT_SYSTEM: 'System calculated:',
  CONFIRM_AMOUNT_ENTERED: 'Entered total:',
  CONFIRM_AMOUNT_INTENTIONAL: 'Continue only if this adjustment is intentional.',
  ADJUSTMENT_REASON_HELPER: 'This reason will be saved in the audit trail.',
  OPTIONAL_NOTE_HELPER: 'Example: repeat guest discount, package rate, billing correction.',
  ROOM_MEMBER_TOTAL: (
    stayDate: string,
    expectedAdults: number,
    expectedChildren: number,
    actualAdults: number,
    actualChildren: number
  ) => `Room allocation for ${stayDate} must total ${expectedAdults} adult(s) and ${expectedChildren} child(ren). Current allocation has ${actualAdults} adult(s) and ${actualChildren} child(ren).`,
  ROOM_NUMBER_REQUIRED: (stayDate: string) => `Select a room number for ${stayDate}`,
  CUSTOM_ROOM_TYPE_REQUIRED: (stayDate: string) => `Enter custom room type for ${stayDate}`,
  ROOM_COUNT_MISMATCH: (stayDate: string) => `Number of rooms must match selected room numbers for ${stayDate}`,
  ROOM_NOT_IN_INVENTORY: (stayDate: string) => `Selected room number does not exist for the selected room type on ${stayDate}`,
  DUPLICATE_ROOM: (stayDate: string) => `Same room cannot be selected more than once for ${stayDate}`,
} as const;

export const GUEST_FORM_TEST_ID = {
  GUEST_NAME: 'field-guest-name',
  CHECKIN: 'field-checkin',
  CHECKOUT: 'field-checkout',
  BOOKING_TYPE: 'field-booking-type',
  TOTAL: 'field-total',
  BOOKED_BY: 'field-booked-by',
  STATUS: 'field-status',
  CANCEL_REASON: 'field-cancel-reason',
  IS_REFUNDED: 'field-is-refunded',
  SYSTEM_CALCULATED_TOTAL: 'system-calculated-total',
  ENTERED_TOTAL: 'entered-total',
  TOTAL_DIFFERENCE: 'total-difference',
  ROOM_NUMBER_SELECT: (dayIdx: number, roomIdx: number) => `room-number-select-${dayIdx}-${roomIdx}`,
  DRIVER_AMOUNT: (dayIdx: number) => `driver-amount-${dayIdx}`,
} as const;

export const GUEST_FORM_FILE_UPLOAD = {
  ACCEPTED_TYPES: 'image/jpeg,image/png,image/webp,image/gif,application/pdf',
  PDF_MIME_TYPE: 'application/pdf',
} as const;
